import React, { useMemo } from 'react';
import Modal from './ui/Modal';
import Button from './ui/Button';
import Card from './ui/Card';
import AcademicYearService from '../services/AcademicYearService';

// Helper to get calendar year for a week/academicYear pair
const getEventDateYear = (weekNumber, academicYearStart) => {
    let targetYear = academicYearStart;
    const academicService = AcademicYearService.getInstance();
    const parsedYear = academicService.getParsedAcademicYear();

    if (parsedYear && parsedYear.weekToYearMap.has(weekNumber)) {
        if (parsedYear.startCalendarYear === academicYearStart) {
            targetYear = parsedYear.weekToYearMap.get(weekNumber);
        }
    } else if (weekNumber < 20) {
        targetYear = academicYearStart + 1;
    }
    return targetYear;
};

const ScheduleEntryDetailView = ({
    isOpen,
    onClose,
    scheduleEntry,
    groups = [],
    subjects = [],
    teachers = [],
    rooms = [],
    isPublic = false
}) => {
    // HTML-aware description renderer (mirrors Event Details modal behavior)
    const isHtmlContent = (text) => {
        if (!text || typeof text !== 'string') return false;
        const htmlTagRegex = /<[^>]+>/;
        return htmlTagRegex.test(text);
    };

    const sanitizeHtml = (html) => {
        if (!html) return '';
        let sanitized = html
            .replace(/<script[^>]*>.*?<\/script>/gi, '')
            .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
            .replace(/<object[^>]*>.*?<\/object>/gi, '')
            .replace(/<embed[^>]*>.*?<\/embed>/gi, '')
            .replace(/<link[^>]*>/gi, '')
            .replace(/<meta[^>]*>/gi, '')
            .replace(/on\w+="[^"]*"/gi, '')
            .replace(/javascript:/gi, '')
            .replace(/data:/gi, '')
            .replace(/vbscript:/gi, '');

        sanitized = sanitized.replace(/<a\s+([^>]*?)>/gi, (match, attributes) => {
            const cleanAttributes = attributes
                .replace(/style="[^"]*"/gi, '')
                .replace(/target="[^"]*"/gi, '')
                .replace(/rel="[^"]*"/gi, '');

            return `<a ${cleanAttributes} style="color: #2563eb; text-decoration: underline;" target="_blank" rel="noopener noreferrer">`;
        });

        return sanitized;
    };

    const renderDescription = (text) => {
        if (!text) return null;
        if (isHtmlContent(text)) {
            const sanitizedHtml = sanitizeHtml(text);
            return (
                <div
                    className="text-sm text-gray-900 whitespace-pre-wrap mt-1 [&_a]:text-blue-600 [&_a]:underline [&_a]:hover:text-blue-800 [&_a]:break-words"
                    dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                />
            );
        }

        const input = String(text);
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const nodes = [];
        let lastIndex = 0;
        let match;
        let key = 0;
        while ((match = urlRegex.exec(input)) !== null) {
            const start = match.index;
            const url = match[0];
            if (start > lastIndex) {
                nodes.push(input.slice(lastIndex, start));
            }
            nodes.push(
                <a
                    key={`desc-url-${key++}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline break-words"
                >
                    {url}
                </a>
            );
            lastIndex = start + url.length;
        }
        if (lastIndex < input.length) {
            nodes.push(input.slice(lastIndex));
        }
        const withLineBreaks = [];
        nodes.forEach((chunk, idx) => {
            if (typeof chunk === 'string') {
                const lines = chunk.split('\n');
                lines.forEach((line, li) => {
                    if (line) withLineBreaks.push(<React.Fragment key={`t-${idx}-${li}`}>{line}</React.Fragment>);
                    if (li < lines.length - 1) withLineBreaks.push(<br key={`br-${idx}-${li}`} />);
                });
            } else {
                withLineBreaks.push(chunk);
            }
        });
        return (
            <div className="text-sm text-gray-900 whitespace-pre-wrap mt-1">
                {withLineBreaks}
            </div>
        );
    };
    // Local util to convert (week, day, year) to YYYY-MM-DD
    const convertScheduleFormatToDate = (weekSeries, day, year) => {
        if (!weekSeries || !day || !year) return '';
        const firstWeek = String(weekSeries).split(',')[0];
        if (!firstWeek) return '';
        const weekNumber = parseInt(firstWeek, 10);
        if (isNaN(weekNumber)) return '';

        const targetYear = getEventDateYear(weekNumber, year);
        const jan4 = new Date(targetYear, 0, 4);
        const dayOfWeek = jan4.getDay() || 7;
        const week1Monday = new Date(jan4);
        week1Monday.setDate(jan4.getDate() - dayOfWeek + 1);

        const targetMonday = new Date(week1Monday);
        targetMonday.setDate(week1Monday.getDate() + (weekNumber - 1) * 7);

        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayIndex = days.indexOf(String(day).toLowerCase());
        if (dayIndex === -1) return '';

        const targetDate = new Date(targetMonday);
        if (dayIndex === 0) {
            targetDate.setDate(targetMonday.getDate() + 6);
        } else {
            targetDate.setDate(targetMonday.getDate() + (dayIndex - 1));
        }
        return targetDate;
    };

    const buildPublicEventDateTime = (entry) => {
        const dateObj = convertScheduleFormatToDate(entry.weekSeries, entry.day, entry.year);
        if (!dateObj) {
            const timeText = entry.timeRangeDisplay ? entry.timeRangeDisplay() : (entry.time || (entry.timeStart && entry.timeEnd ? `${entry.timeStart} - ${entry.timeEnd}` : ''));
            return timeText || 'N/A';
        }
        const datePart = dateObj.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
        const timeText = entry.timeRangeDisplay ? entry.timeRangeDisplay() : (entry.time || (entry.timeStart && entry.timeEnd ? `${entry.timeStart} - ${entry.timeEnd}` : ''));
        return timeText ? `${datePart}, ${timeText}` : datePart;
    };

    // Helpers for public class schedule summary
    const toOrdinal = (n) => {
        const s = ['th', 'st', 'nd', 'rd'];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    const formatHumanDate = (dateObj) => {
        if (!dateObj) return '';
        const weekday = dateObj.toLocaleDateString(undefined, { weekday: 'long' });
        const dayNum = dateObj.getDate();
        const month = dateObj.toLocaleDateString(undefined, { month: 'long' });
        const year = dateObj.getFullYear();
        return `${weekday} ${toOrdinal(dayNum)} ${month} ${year}`;
    };

    const getDayIndex = (day) => {
        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        return days.indexOf(String(day).toLowerCase());
    };

    const weekAndDayToDate = (year, weekNumber, day) => {
        if (!year || !weekNumber || !day) return null;
        const targetYear = getEventDateYear(weekNumber, year);
        const jan4 = new Date(targetYear, 0, 4);
        const dayOfWeek = jan4.getDay() || 7;
        const week1Monday = new Date(jan4);
        week1Monday.setDate(jan4.getDate() - dayOfWeek + 1);
        const targetMonday = new Date(week1Monday);
        targetMonday.setDate(week1Monday.getDate() + (weekNumber - 1) * 7);
        const dayIndex = getDayIndex(day);
        if (dayIndex === -1) return null;
        const targetDate = new Date(targetMonday);
        if (dayIndex === 0) {
            targetDate.setDate(targetMonday.getDate() + 6);
        } else {
            targetDate.setDate(targetMonday.getDate() + (dayIndex - 1));
        }
        return targetDate;
    };

    const parseWeekSeriesToDates = (weekSeriesRaw, year, day) => {
        const result = [];
        if (!weekSeriesRaw || !year || !day) return result;
        const parts = String(weekSeriesRaw).split(',').map(p => p.trim()).filter(Boolean);
        parts.forEach(part => {
            if (part.includes('-')) {
                const [startStr, endStr] = part.split('-').map(s => s.trim());
                const start = parseInt(startStr, 10);
                const end = parseInt(endStr, 10);
                if (isNaN(start) || isNaN(end)) return;
                if (start <= end) {
                    for (let w = start; w <= end; w++) {
                        const d = weekAndDayToDate(year, w, day);
                        if (d) result.push(d);
                    }
                } else {
                    // crossing year: start..53 in current year, then 1..end in next year
                    for (let w = start; w <= 53; w++) {
                        const d = weekAndDayToDate(year, w, day);
                        if (d) result.push(d);
                    }
                    for (let w = 1; w <= end; w++) {
                        const d = weekAndDayToDate(year, w, day);
                        if (d) result.push(d);
                    }
                }
            } else {
                const w = parseInt(part, 10);
                if (!isNaN(w)) {
                    const d = weekAndDayToDate(year, w, day);
                    if (d) result.push(d);
                }
            }
        });
        // Sort dates ascending
        result.sort((a, b) => a.getTime() - b.getTime());
        return result;
    };

    const buildPublicClassScheduleSummary = (entry) => {
        const dates = parseWeekSeriesToDates(entry.weekSeries, entry.year, entry.day);
        if (dates.length === 0) return 'N/A';
        if (dates.length <= 5) {
            return dates.map(d => formatHumanDate(d));
        }
        const dayPlural = (String(entry.day || '')).charAt(0).toUpperCase() + String(entry.day || '').slice(1) + 's';
        const first = dates[0];
        const last = dates[dates.length - 1];
        return `${dayPlural} from ${formatHumanDate(first)} until ${formatHumanDate(last)}`;
    };

    // Build Google Calendar URL for the event/class
    const getTimeZone = () => {
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
        } catch (_) {
            return undefined;
        }
    };

    const parseTimeRangeFromEntry = (entry) => {
        // Returns { startMinutes, endMinutes } from 00:00 of the given date
        const parseHHMM = (str) => {
            if (!str || typeof str !== 'string') return null;
            const match = str.trim().match(/^(\d{1,2}):(\d{2})$/);
            if (!match) return null;
            const hours = parseInt(match[1], 10);
            const minutes = parseInt(match[2], 10);
            if (isNaN(hours) || isNaN(minutes)) return null;
            return hours * 60 + minutes;
        };

        const fromRangeString = (s) => {
            if (!s || typeof s !== 'string') return null;
            const parts = s.split('-').map(p => p.trim());
            if (parts.length !== 2) return null;
            const start = parseHHMM(parts[0]);
            const end = parseHHMM(parts[1]);
            if (start == null || end == null) return null;
            return { startMinutes: start, endMinutes: end };
        };

        // Try timeRangeDisplay()
        if (typeof entry.timeRangeDisplay === 'function') {
            const display = entry.timeRangeDisplay();
            const parsed = fromRangeString(display);
            if (parsed) return parsed;
        }
        // Try entry.time
        if (entry.time) {
            const parsed = fromRangeString(entry.time);
            if (parsed) return parsed;
        }
        // Try timeStart/timeEnd
        if (entry.timeStart && entry.timeEnd) {
            const start = parseHHMM(entry.timeStart);
            const end = parseHHMM(entry.timeEnd);
            if (start != null && end != null) return { startMinutes: start, endMinutes: end };
        }
        // Fallback with durationMinutes if only start is provided
        if (entry.timeStart && entry.durationMinutes) {
            const start = parseHHMM(entry.timeStart);
            const end = start != null ? start + Number(entry.durationMinutes) : null;
            if (start != null && end != null) return { startMinutes: start, endMinutes: end };
        }
        return null;
    };

    const formatDateTimeForGoogle = (dateObj) => {
        const pad = (n) => String(n).padStart(2, '0');
        const year = dateObj.getFullYear();
        const month = pad(dateObj.getMonth() + 1);
        const day = pad(dateObj.getDate());
        const hours = pad(dateObj.getHours());
        const minutes = pad(dateObj.getMinutes());
        const seconds = pad(dateObj.getSeconds());
        // Local time; we will pass `ctz` to specify timezone
        return `${year}${month}${day}T${hours}${minutes}${seconds}`;
    };

    const pickEventDate = (entry) => {
        // For events, try direct conversion using first week/day
        const maybe = convertScheduleFormatToDate(entry.weekSeries, entry.day, entry.year);
        if (maybe) return maybe;
        return null;
    };

    const pickClassDate = (entry) => {
        const dates = parseWeekSeriesToDates(entry.weekSeries, entry.year, entry.day);
        if (dates.length === 0) return null;
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const next = dates.find(d => d >= todayStart);
        return next || dates[0];
    };

    const buildGoogleCalendarUrl = (entry) => {
        if (!entry) return null;

        const title = entry.type === 'event'
            ? (entry.title || entry.eventName || 'Event')
            : (entry.subject || entry.course || 'Class');

        const descriptionParts = [];
        if (entry.description) descriptionParts.push(String(entry.description));
        if (entry.host || entry.teacher || entry.teachersFormatted) {
            const hostLabel = entry.host || entry.teacher || entry.teachersFormatted;
            const prefix = entry.type === 'event' ? 'Host' : 'Teacher';
            descriptionParts.push(`${prefix}: ${hostLabel}`);
        }
        const details = descriptionParts.join('\n');

        const location = entry.location || entry.room || '';

        const timeRange = parseTimeRangeFromEntry(entry);
        if (!timeRange) return null; // Cannot build without times

        const baseDate = entry.type === 'event' ? pickEventDate(entry) : pickClassDate(entry);
        if (!baseDate) return null;

        // Build start/end local Date objects
        const start = new Date(baseDate);
        start.setHours(0, 0, 0, 0);
        start.setMinutes(start.getMinutes() + timeRange.startMinutes);

        const end = new Date(baseDate);
        end.setHours(0, 0, 0, 0);
        end.setMinutes(end.getMinutes() + timeRange.endMinutes);

        const datesParam = `${formatDateTimeForGoogle(start)}/${formatDateTimeForGoogle(end)}`;
        const tz = getTimeZone();

        const params = new URLSearchParams();
        params.set('action', 'TEMPLATE');
        params.set('text', title);
        if (details) params.set('details', details);
        if (location) params.set('location', location);
        params.set('dates', datesParam);
        if (tz) params.set('ctz', tz);

        return `https://calendar.google.com/calendar/render?${params.toString()}`;
    };

    const googleCalendarUrl = useMemo(() => buildGoogleCalendarUrl(scheduleEntry), [scheduleEntry]);
    // Helper function to format group names
    const formatGroups = useMemo(() => {
        if (!scheduleEntry?.groups || scheduleEntry.groups.length === 0) {
            return 'All Groups';
        }

        return scheduleEntry.groups.map(g => {
            if (typeof g === 'object' && g !== null) {
                return g.name || g.group_code || g.id;
            }
            // Handle case where g is an ID, find the group
            const group = groups.find(group => group.id === g || group.name === g || group.group_code === g);
            return group ? (group.name || group.group_code) : g;
        }).join(', ');
    }, [scheduleEntry?.groups, groups]);

    // Helper function to format exceptions
    const formatExceptions = useMemo(() => {
        if (!scheduleEntry?.exceptions) {
            return [];
        }

        try {
            let parsedExceptions;
            if (typeof scheduleEntry.exceptions === 'string') {
                parsedExceptions = JSON.parse(scheduleEntry.exceptions);
            } else {
                parsedExceptions = scheduleEntry.exceptions;
            }

            if (!Array.isArray(parsedExceptions)) {
                return [];
            }

            return parsedExceptions.map((exception, index) => ({
                id: index,
                date: exception.target_occurrence_date,
                changeType: exception.change_type,
                reason: exception.reason || '',
                newRoom: exception.new_room_id ?
                    rooms.find(r => r.id === exception.new_room_id)?.name || `Room ID: ${exception.new_room_id}` :
                    exception.new_room_name || null,
                newTeacher: exception.new_teacher_id ?
                    teachers.find(t => t.id === exception.new_teacher_id)?.name || `Teacher ID: ${exception.new_teacher_id}` :
                    exception.new_teacher_name || null,
                newStartTime: exception.new_start_time,
                newEndTime: exception.new_end_time,
                newDay: exception.new_day,
                raw: exception
            }));
        } catch (error) {
            console.error('Error parsing exceptions:', error);
            return [];
        }
    }, [scheduleEntry?.exceptions, rooms, teachers]);

    // Helper function to get change type display
    const getChangeTypeDisplay = (changeType) => {
        const changeTypeMap = {
            'cancelled': 'Cancelled',
            'rescheduled': 'Rescheduled',
            'room_change': 'Room Change',
            'teacher_change': 'Teacher Change',
            'time_change': 'Time Change',
            'day_change': 'Day Change'
        };
        return changeTypeMap[changeType] || changeType;
    };

    // Helper function to get change type color
    const getChangeTypeColor = (changeType) => {
        const colorMap = {
            'cancelled': 'text-red-600 bg-red-100',
            'rescheduled': 'text-blue-600 bg-blue-100',
            'room_change': 'text-yellow-600 bg-yellow-100',
            'teacher_change': 'text-purple-600 bg-purple-100',
            'time_change': 'text-green-600 bg-green-100',
            'day_change': 'text-indigo-600 bg-indigo-100'
        };
        return colorMap[changeType] || 'text-gray-600 bg-gray-100';
    };

    // Convert week series to human readable format
    const formatWeekSeries = (weekSeries) => {
        if (!weekSeries) return 'N/A';

        // Convert week ranges to more readable format
        return weekSeries.split(',').map(range => {
            if (range.includes('-')) {
                const [start, end] = range.split('-');
                return `Week ${start} to Week ${end}`;
            }
            return `Week ${range}`;
        }).join(', ');
    };

    if (!scheduleEntry) {
        return null;
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={scheduleEntry?.type === 'event' ? 'Event Details' : 'Class Details'}
            className="max-w-4xl"
        >
            <div className="space-y-6">
                {/* Header Information */}
                <Card className="p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {scheduleEntry.type === 'event' ?
                            (scheduleEntry.title || scheduleEntry.eventName || 'Event') :
                            (scheduleEntry.subject || scheduleEntry.course || 'Class')
                        }
                    </h3>
                    {isPublic && scheduleEntry.type === 'event' && (
                        <p className="text-sm text-gray-700 mb-3">
                            {scheduleEntry.host || scheduleEntry.teacher || scheduleEntry.teachersFormatted || 'N/A'}
                        </p>
                    )}
                    {isPublic && scheduleEntry.type !== 'event' && scheduleEntry.teacher && (
                        <p className="text-md font-semibold text-gray-500 mb-3">with {scheduleEntry.teacher}</p>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {!isPublic && (
                            <>
                                <div>
                                    <span className="text-sm font-medium text-gray-600">Entry ID:</span>
                                    <p className="text-sm text-gray-900">{scheduleEntry.id}</p>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-600">Type:</span>
                                    <p className="text-sm text-gray-900 capitalize">{scheduleEntry.type || 'class'}</p>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-600">Year:</span>
                                    <p className="text-sm text-gray-900">{scheduleEntry.year}</p>
                                </div>
                            </>
                        )}
                    </div>
                </Card>

                {/* Schedule Information */}
                <Card className="p-4">
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Schedule Information</h4>
                    {isPublic && scheduleEntry.type === 'event' ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <span className="text-sm font-medium text-gray-600">Date</span>
                                <p className="text-sm text-gray-900">{buildPublicEventDateTime(scheduleEntry)}</p>
                            </div>
                            <div>
                                <span className="text-sm font-medium text-gray-600">Room</span>
                                <p className="text-sm text-gray-900">{scheduleEntry.room || 'N/A'}</p>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {!isPublic && (
                                <div>
                                    <span className="text-sm font-medium text-gray-600">Week Series:</span>
                                    <p className="text-sm text-gray-900">{formatWeekSeries(scheduleEntry.weekSeries)}</p>
                                </div>
                            )}
                            {isPublic ? (
                                <div className="col-span-full space-y-1">
                                    {(() => {
                                        const summary = buildPublicClassScheduleSummary(scheduleEntry);
                                        return Array.isArray(summary) ? (
                                            summary.map((line, idx) => (
                                                <p key={idx} className="text-sm text-gray-900">{line}</p>
                                            ))
                                        ) : (
                                            <p className="text-sm text-gray-900">{summary}</p>
                                        );
                                    })()}
                                </div>
                            ) : (
                                <>
                                    <div>
                                        <span className="text-sm font-medium text-gray-600">Day:</span>
                                        <p className="text-sm text-gray-900 capitalize">{scheduleEntry.day}</p>
                                    </div>
                                    <div>
                                        <span className="text-sm font-medium text-gray-600">Time:</span>
                                        <p className="text-sm text-gray-900">
                                            {scheduleEntry.timeRangeDisplay ?
                                                scheduleEntry.timeRangeDisplay() :
                                                scheduleEntry.time ||
                                                (scheduleEntry.timeStart && scheduleEntry.timeEnd ?
                                                    `${scheduleEntry.timeStart} - ${scheduleEntry.timeEnd}` :
                                                    'Not specified'
                                                )
                                            }
                                        </p>
                                    </div>
                                </>
                            )}
                            {!isPublic && (
                                <div>
                                    <span className="text-sm font-medium text-gray-600">Duration:</span>
                                    <p className="text-sm text-gray-900">
                                        {scheduleEntry.durationMinutes ? `${scheduleEntry.durationMinutes} minutes` : 'N/A'}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </Card>

                {/* Content Information */}
                <Card className="p-4">
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Content Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {scheduleEntry.type === 'event' ? (
                            isPublic ? (
                                <>
                                    {scheduleEntry.description && (
                                        <div className="col-span-full">
                                            <span className="text-sm font-medium text-gray-600">Description:</span>
                                            {renderDescription(scheduleEntry.description)}
                                        </div>
                                    )}
                                    <div>
                                        <span className="text-sm font-medium text-gray-600">Groups:</span>
                                        <p className="text-sm text-gray-900">{formatGroups}</p>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div>
                                        <span className="text-sm font-medium text-gray-600">Event Name:</span>
                                        <p className="text-sm text-gray-900">{scheduleEntry.title || scheduleEntry.eventName || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <span className="text-sm font-medium text-gray-600">Host:</span>
                                        <p className="text-sm text-gray-900">
                                            {scheduleEntry.host || scheduleEntry.teacher || scheduleEntry.teachersFormatted || 'N/A'}
                                        </p>
                                    </div>
                                    {scheduleEntry.description && (
                                        <div className="col-span-full">
                                            <span className="text-sm font-medium text-gray-600">Description:</span>
                                            {renderDescription(scheduleEntry.description)}
                                        </div>
                                    )}
                                    {scheduleEntry.location && (
                                        <div>
                                            <span className="text-sm font-medium text-gray-600">Location:</span>
                                            <p className="text-sm text-gray-900">{scheduleEntry.location}</p>
                                        </div>
                                    )}
                                </>
                            )
                        ) : (
                            isPublic ? (
                                <>
                                    <div>
                                        <span className="text-sm font-medium text-gray-600">Room:</span>
                                        <p className="text-sm text-gray-900">{scheduleEntry.room || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <span className="text-sm font-medium text-gray-600">Groups:</span>
                                        <p className="text-sm text-gray-900">{formatGroups}</p>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div>
                                        <span className="text-sm font-medium text-gray-600">Subject:</span>
                                        <p className="text-sm text-gray-900">{scheduleEntry.subject || scheduleEntry.course || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <span className="text-sm font-medium text-gray-600">Teacher:</span>
                                        <p className="text-sm text-gray-900">{scheduleEntry.teacher || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <span className="text-sm font-medium text-gray-600">Room:</span>
                                        <p className="text-sm text-gray-900">{scheduleEntry.room || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <span className="text-sm font-medium text-gray-600">Groups:</span>
                                        <p className="text-sm text-gray-900">{formatGroups}</p>
                                    </div>
                                </>
                            )
                        )}
                    </div>
                </Card>

                {/* Technical Information */}
                {!isPublic && (
                    <Card className="p-4">
                        <h4 className="text-md font-semibold text-gray-900 mb-3">Technical Information</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <span className="text-sm font-medium text-gray-600">Period ID:</span>
                                <p className="text-sm text-gray-900">{scheduleEntry.periodId || 'N/A'}</p>
                            </div>
                            <div>
                                <span className="text-sm font-medium text-gray-600">Client ID:</span>
                                <p className="text-sm text-gray-900">{scheduleEntry.additionalProps?.client_id || 'N/A'}</p>
                            </div>
                            <div>
                                <span className="text-sm font-medium text-gray-600">Draft Name:</span>
                                <p className="text-sm text-gray-900">{scheduleEntry.additionalProps?.draft_name || 'Live (no draft)'}</p>
                            </div>
                            {scheduleEntry.notes && (
                                <div className="col-span-full">
                                    <span className="text-sm font-medium text-gray-600">Notes:</span>
                                    <p className="text-sm text-gray-900 whitespace-pre-wrap mt-1">{scheduleEntry.notes}</p>
                                </div>
                            )}
                        </div>
                    </Card>
                )}

                {/* Exceptions Section */}
                {!isPublic && (
                    <Card className="p-4">
                        <h4 className="text-md font-semibold text-gray-900 mb-3">
                            Exceptions
                            <span className="text-sm font-normal text-gray-600 ml-2">
                                (Weekly modifications)
                            </span>
                        </h4>

                        {formatExceptions.length === 0 ? (
                            <p className="text-sm text-gray-500 italic">No exceptions defined for this schedule entry.</p>
                        ) : (
                            <div className="space-y-3">
                                {formatExceptions.map((exception) => (
                                    <div key={exception.id} className="border border-gray-200 rounded-lg p-3">
                                        <div className="flex items-start justify-between mb-2">
                                            <div className="flex items-center space-x-2">
                                                <span className="text-sm font-medium text-gray-900">
                                                    {new Date(exception.date).toLocaleDateString()}
                                                </span>
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getChangeTypeColor(exception.changeType)}`}>
                                                    {getChangeTypeDisplay(exception.changeType)}
                                                </span>
                                            </div>
                                        </div>

                                        {exception.reason && (
                                            <div className="mb-2">
                                                <span className="text-xs font-medium text-gray-600">Reason:</span>
                                                <p className="text-xs text-gray-900 mt-1">{exception.reason}</p>
                                            </div>
                                        )}

                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                            {exception.newRoom && (
                                                <div>
                                                    <span className="font-medium text-gray-600">New Room:</span>
                                                    <p className="text-gray-900">{exception.newRoom}</p>
                                                </div>
                                            )}
                                            {exception.newTeacher && (
                                                <div>
                                                    <span className="font-medium text-gray-600">New Teacher:</span>
                                                    <p className="text-gray-900">{exception.newTeacher}</p>
                                                </div>
                                            )}
                                            {exception.newStartTime && exception.newEndTime && (
                                                <div>
                                                    <span className="font-medium text-gray-600">New Time:</span>
                                                    <p className="text-gray-900">{exception.newStartTime} - {exception.newEndTime}</p>
                                                </div>
                                            )}
                                            {exception.newDay && (
                                                <div>
                                                    <span className="font-medium text-gray-600">New Day:</span>
                                                    <p className="text-gray-900 capitalize">{exception.newDay}</p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Raw exception data (collapsible) */}
                                        <details className="mt-2">
                                            <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                                                View raw exception data
                                            </summary>
                                            <pre className="text-xs text-gray-600 mt-1 p-2 bg-gray-50 rounded overflow-auto">
                                                {JSON.stringify(exception.raw, null, 2)}
                                            </pre>
                                        </details>
                                    </div>
                                ))}
                            </div>
                        )}
                    </Card>
                )}

                {/* Additional Properties */}
                {!isPublic && Object.keys(scheduleEntry.additionalProps || {}).length > 0 && (
                    <Card className="p-4">
                        <h4 className="text-md font-semibold text-gray-900 mb-3">Additional Properties</h4>
                        <div className="space-y-2">
                            {Object.entries(scheduleEntry.additionalProps).map(([key, value]) => (
                                <div key={key} className="grid grid-cols-3 gap-2">
                                    <span className="text-sm font-medium text-gray-600">{key}:</span>
                                    <span className="text-sm text-gray-900 col-span-2">
                                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </Card>
                )}

                {/* Raw Data (collapsible) */}
                {!isPublic && (
                    <details className="mt-6">
                        <summary className="text-sm text-gray-500 cursor-pointer hover:text-gray-700 font-medium">
                            View raw schedule entry data
                        </summary>
                        <Card className="p-4 mt-2">
                            <pre className="text-xs text-gray-600 overflow-auto max-h-64">
                                {JSON.stringify(scheduleEntry.toObject ? scheduleEntry.toObject() : scheduleEntry, null, 2)}
                            </pre>
                        </Card>
                    </details>
                )}

                {/* Actions */}
                <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-200">
                    {googleCalendarUrl && (
                        <Button
                            variant="primary"
                            onClick={() => window.open(googleCalendarUrl, '_blank', 'noopener,noreferrer')}
                            title="Add to Google Calendar"
                            aria-label="Add to Google Calendar"
                        >
                            Add to Google Calendar
                        </Button>
                    )}
                    <Button variant="secondary" onClick={onClose}>
                        Close
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default ScheduleEntryDetailView; 